import React, { useState } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription,
  DialogFooter
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Bot, FileText, Upload, Sparkles, Loader2, AlertCircle, Settings, Download } from 'lucide-react';

const AgentCreatorModal = ({ isOpen, onClose, onAgentCreated }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [progressText, setProgressText] = useState("");
  const [createdAgent, setCreatedAgent] = useState(null);

  // Form State
  const [agentName, setAgentName] = useState("");
  const [scriptText, setScriptText] = useState("");
  const [scriptFile, setScriptFile] = useState(null);
  const [kbFiles, setKbFiles] = useState([]);
  const [customInstructions, setCustomInstructions] = useState("");
  const [prospectIntelligence, setProspectIntelligence] = useState("");
  
  const [creatorLlm, setCreatorLlm] = useState("gpt-5.5");
  const [runtimeLlm, setRuntimeLlm] = useState("grok-4-fast-non-reasoning");
  const [ttsProvider, setTtsProvider] = useState("cartesia");
  const [sttProvider, setSttProvider] = useState("deepgram");
  const [voice, setVoice] = useState("Rachel");
  const [language, setLanguage] = useState("English");
  
  const [tightPatterns, setTightPatterns] = useState(false);
  const [strictKb, setStrictKb] = useState(false);
  const [useBehavioralPlaybook, setUseBehavioralPlaybook] = useState(true);
  const [agentPersonaName, setAgentPersonaName] = useState("");

  const handleScriptUpload = (e) => {
    if (e.target.files && e.target.files[0]) {
      setScriptFile(e.target.files[0]);
    }
  };

  const handleKBUpload = (e) => {
    if (e.target.files) {
      setKbFiles(prev => [...prev, ...Array.from(e.target.files)]);
    }
    // Reset input so the same file can be re-added if removed
    e.target.value = '';
  };

  const removeKBFile = (index) => {
    setKbFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleGenerate = async () => {
    if (!scriptText && !scriptFile) {
      setError("Please provide a script or transcript (text or file) to continue.");
      return;
    }
    
    setError(null);
    setLoading(true);
    setStep(3);
    setProgressText("Initializing AI Architect...");

    try {
      const formData = new FormData();
      if (scriptFile) formData.append('script_file', scriptFile);
      if (scriptText) formData.append('script_text', scriptText);
      
      kbFiles.forEach(file => {
        formData.append('kb_files', file);
      });

      formData.append('creator_llm', creatorLlm);
      formData.append('runtime_llm', runtimeLlm);
      formData.append('tts_provider', ttsProvider);
      formData.append('stt_provider', sttProvider);
      formData.append('voice', voice);
      formData.append('language', language);
      formData.append('agent_name', agentName || "New Auto-Generated Agent");
      formData.append('agent_persona_name', agentPersonaName);
      formData.append('custom_instructions', customInstructions);
      formData.append('prospect_intelligence', prospectIntelligence);
      formData.append('tight_patterns', tightPatterns);
      formData.append('strict_kb', strictKb);
      formData.append('use_behavioral_playbook', useBehavioralPlaybook);

      const token = localStorage.getItem('token');
      
      setProgressText(`Analyzing script & KBs using ${creatorLlm}...`);
      
      // Cycle through progress messages to simulate the different phases
      const messages = [
        `Analyzing script & KBs using ${creatorLlm}...`,
        "Generating Node Graph...",
        "Applying Self-Healing & Transition Fixes...",
        prospectIntelligence ? "Running Predictive Node QC (simulating prospect responses)..." : "Optimizing transitions...",
        "Running Segmented Auto-Tests (Intro, Pitch, Close)...",
        "Finalizing Validation..."
      ];
      
      let messageIndex = 0;
      const progressInterval = setInterval(() => {
        messageIndex = Math.min(messageIndex + 1, messages.length - 1);
        setProgressText(messages[messageIndex]);
      }, 25000); // Update message every 25 seconds
      
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
        const response = await fetch(`${backendUrl}/api/creator/generate`, {
          method: 'POST',
          credentials: 'include',
          body: formData
        });

        let responseData;
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.indexOf("application/json") !== -1) {
          responseData = await response.json();
        } else {
          responseData = { detail: await response.text() };
        }

        if (!response.ok) {
          throw new Error(responseData.detail || 'Failed to start generation job');
        }

        const jobId = responseData.job_id;
        
        // Polling Loop
        const pollInterval = setInterval(async () => {
          try {
            const statusRes = await fetch(`${backendUrl}/api/creator/status/${jobId}`, {
              credentials: 'include'
            });
            
            if (!statusRes.ok) return;
            const statusData = await statusRes.json();
            
            setProgressText(`[${statusData.progress}%] ${statusData.message}`);
            
            if (statusData.status === 'completed') {
              clearInterval(pollInterval);
              setProgressText("Agent generated successfully! Saving to database...");
              
              if (onAgentCreated && statusData.agent_id) {
                onAgentCreated(statusData.agent_id);
              }

              // Fetch agent to get the sandbox report
              try {
                const agentRes = await fetch(`${backendUrl}/api/agents/${statusData.agent_id}`, { credentials: 'include' });
                if (agentRes.ok) {
                  const agentData = await agentRes.json();
                  setCreatedAgent(agentData);
                }
              } catch (e) {
                console.error("Failed to fetch created agent details", e);
              }
              
              setStep(4); // Success step
            } else if (statusData.status === 'failed') {
              clearInterval(pollInterval);
              setError(statusData.message || "Background job failed.");
              setStep(2);
            }
          } catch (pollErr) {
            console.error("Polling error:", pollErr);
          }
        }, 3000); // Poll every 3 seconds

      } catch (err) {
        setError(err.message);
        setStep(2); // Go back so they can try again
      } finally {
        // We do NOT set loading to false here, because the polling loop is running in the background
        // The modal step handles the UI state.
      }
    } catch (err) {
      setError(err.message);
      setStep(2);
      setLoading(false);
    }
  };

  const downloadMarkdownReport = () => {
    if (!createdAgent || !createdAgent.sandbox_stress_test_report) return;
    const report = createdAgent.sandbox_stress_test_report;
    const agentName = createdAgent.name || "Agent";
    
    let md = `# Phase 2.9 Sandbox Stress Test Report\n\n`;
    md += `**Agent Name:** ${agentName}\n`;
    md += `**Runtime Model:** ${report.runtime_model || "Unknown"}\n\n`;
    
    md += `## Summary\n`;
    md += `- **Nodes Tested:** ${report.nodes_tested || 0}\n`;
    md += `- **Passed (1st try):** ${report.nodes_passed || 0}\n`;
    md += `- **Auto-Fixed:** ${report.nodes_fixed || 0}\n`;
    md += `- **Failed:** ${report.nodes_failed || 0}\n`;
    md += `- **Avg Latency (TTFT):** ${report.avg_actor_latency_ms || 0}ms\n`;
    md += `- **Max Latency:** ${report.max_actor_latency_ms || 0}ms\n`;
    md += `- **Total Rewrites:** ${report.rewrites_performed || 0}\n\n`;
    
    md += `## Detailed Breakdown\n\n`;
    
    if (report.details && report.details.length > 0) {
      report.details.forEach(d => {
        md += `### ${d.node_label || d.node_id}\n`;
        md += `- **Status:** ${d.status.toUpperCase()}\n`;
        md += `- **Rewrites Required:** ${d.rewrites}\n`;
        md += `- **Final Latency:** ${d.final_latency_ms}ms\n`;
        
        if (d.failure_history && d.failure_history.length > 0) {
          md += `\n#### Failure History:\n`;
          d.failure_history.forEach((fh) => {
            md += `\n**Iteration ${fh.iteration + 1} Failures:**\n`;
            fh.failures.forEach(f => {
              const statusStr = (!f.correct ? "WRONG ROUTE" : (!f.fast ? `SLOW (${f.ttft_ms}ms)` : "PASS"));
              md += `- Input: "${f.test_input}"\n`;
              md += `  - Expected: T:${f.expected} -> Actual: T:${f.actual} [${statusStr}]\n`;
            });
          });
        }
        
        md += `\n#### Conditions Before:\n`;
        d.conditions_before.forEach((c, i) => {
          md += `- Option ${i}: ${c}\n`;
        });
        
        if (d.conditions_after && d.conditions_after.length > 0) {
          md += `\n#### Conditions After (Rewritten):\n`;
          d.conditions_after.forEach((c, i) => {
            md += `- Option ${i}: ${c}\n`;
          });
        }
        
        md += `\n---\n\n`;
      });
    } else {
      md += `No detailed node data available.\n`;
    }
    
    const blob = new Blob([md], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sandbox_report_${agentName.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const resetAndClose = () => {
    setStep(1);
    setScriptText("");
    setScriptFile(null);
    setKbFiles([]);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && resetAndClose()}>
      <DialogContent className="sm:max-w-[700px] h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl font-bold">
            <Sparkles className="h-6 w-6 text-purple-500" />
            AI Agent Creator
          </DialogTitle>
          <DialogDescription>
            Upload your script and Knowledge Bases. Our elite AI will autonomously map out the nodes, logic, and global prompt for you.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-2 py-4">
          {error && (
            <div className="mb-4 p-4 bg-red-50 text-red-600 rounded-md flex items-center gap-2">
              <AlertCircle className="h-5 w-5" />
              <p>{error}</p>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-6">
              <div className="space-y-2">
                <Label className="text-lg font-semibold">1. Primary Script or Transcript</Label>
                <p className="text-sm text-gray-500">Provide the main script or a raw call transcript for the AI to analyze.</p>
                
                <div className="grid grid-cols-2 gap-4 mt-2">
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 flex flex-col items-center justify-center bg-gray-50 hover:bg-gray-100 transition-colors">
                    <FileText className="h-8 w-8 text-gray-400 mb-2" />
                    <Label htmlFor="script-upload" className="cursor-pointer text-sm font-medium text-blue-600 hover:text-blue-500">
                      Upload File (TXT, PDF)
                    </Label>
                    <Input id="script-upload" type="file" className="hidden" onChange={handleScriptUpload} />
                    {scriptFile && <p className="mt-2 text-xs text-gray-600 font-semibold">{scriptFile.name}</p>}
                  </div>
                  
                  <div className="flex flex-col h-full">
                    <Textarea 
                      placeholder="Or paste your script here..." 
                      className="flex-1 min-h-[120px] resize-none"
                      value={scriptText}
                      onChange={(e) => setScriptText(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2 pt-4 border-t">
                <Label className="text-lg font-semibold">2. Knowledge Bases (Optional)</Label>
                <p className="text-sm text-gray-500">Upload company info, customer avatars, or objection frameworks. The AI uses these to build robust objection handles.</p>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 flex flex-col items-center justify-center bg-gray-50 hover:bg-gray-100 transition-colors">
                  <Upload className="h-8 w-8 text-gray-400 mb-2" />
                  <Label htmlFor="kb-upload" className="cursor-pointer text-sm font-medium text-blue-600 hover:text-blue-500">
                    Upload KB Documents
                  </Label>
                  <Input id="kb-upload" type="file" multiple className="hidden" onChange={handleKBUpload} />
                  {kbFiles.length > 0 && (
                    <div className="mt-3 space-y-1">
                      <p className="text-xs font-medium text-gray-500">{kbFiles.length} document{kbFiles.length > 1 ? 's' : ''} uploaded</p>
                      {kbFiles.map((f, idx) => (
                        <div key={`${f.name}-${idx}`} className="flex items-center justify-between bg-white border border-gray-200 rounded px-3 py-1.5 text-xs">
                          <span className="text-gray-700 truncate mr-2">{f.name}</span>
                          <button
                            type="button"
                            onClick={() => removeKBFile(idx)}
                            className="text-red-400 hover:text-red-600 font-bold text-sm flex-shrink-0"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-2 pt-4 border-t">
                <Label className="text-lg font-semibold">3. Custom Instructions (Optional)</Label>
                <p className="text-sm text-gray-500">Add any additional context or specific rules for the AI Architect to consider when designing the flow.</p>
                <Textarea 
                  placeholder="e.g. Make sure to emphasize our 30-day guarantee, and act like a highly energetic sales coach." 
                  className="w-full min-h-[80px] resize-none"
                  value={customInstructions}
                  onChange={(e) => setCustomInstructions(e.target.value)}
                />
              </div>

              <div className="space-y-2 pt-4 border-t">
                <Label className="text-lg font-semibold flex items-center gap-2">
                  <Bot className="h-5 w-5 text-amber-500" />
                  4. Prospect Intelligence (Optional)
                </Label>
                <p className="text-sm text-gray-500">
                  Paste behavioral intelligence — psychology frameworks, expected prospect responses per phase, 
                  derailment patterns, objection playbooks. The AI will simulate realistic prospect reactions at 
                  every node and auto-fix gaps it finds.
                </p>
                <Textarea 
                  placeholder={"e.g. Paste your prospect simulation data, expected responses per phase, objection patterns...\n\nExample:\n- Emotional buyers at the pitch phase say: \"That would definitely help\", \"Okay… what would that make the price?\"\n- Product-aware buyers DERAIL with: \"Discount on what exact monthly price?\", \"Are labs included?\"\n- Weak commitment language: \"I guess\", \"maybe\", \"probably\""}
                  className="w-full min-h-[140px] resize-none font-mono text-xs"
                  value={prospectIntelligence}
                  onChange={(e) => setProspectIntelligence(e.target.value)}
                />
                {prospectIntelligence && (
                  <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                    ✨ Prospect Intelligence loaded ({prospectIntelligence.length.toLocaleString()} chars). 
                    The AI will simulate responses against every node and auto-fix gaps.
                  </p>
                )}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <div className="space-y-4">
                <Label className="text-lg font-semibold flex items-center gap-2">
                  <Bot className="h-5 w-5 text-blue-500" />
                  Creator LLM (The Architect)
                </Label>
                <p className="text-sm text-gray-500">Select the SOTA model that will design your agent's logic paths and objection handles.</p>
                <Select value={creatorLlm} onValueChange={setCreatorLlm}>
                  <SelectTrigger className="w-full h-12 text-lg">
                    <SelectValue placeholder="Select Creator LLM" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hermes">Hermes Agent (ChatGPT OAuth)</SelectItem>
                    <SelectItem value="grok-4.3">Grok 4.3 (Latest Flagship)</SelectItem>
                    <SelectItem value="grok-4-1-fast-non-reasoning">Grok 4.1 Fast Non-Reasoning</SelectItem>
                    <SelectItem value="grok-voice-think-fast-1.0">Grok Voice Think Fast 1.0 (Low Latency)</SelectItem>
                    <SelectItem value="grok-4.20-non-reasoning">Grok 4.20 Non-Reasoning (Fast)</SelectItem>
                    <SelectItem value="grok-4-fast-non-reasoning">Grok 4 Fast Non-Reasoning</SelectItem>
                    <SelectItem value="grok-4-fast-reasoning">Grok 4 Fast Reasoning (Deeper Analysis)</SelectItem>
                    <SelectItem value="grok-3">Grok 3</SelectItem>
                    <SelectItem value="grok-2-1212">Grok 2 (Dec 2024)</SelectItem>
                    <SelectItem value="grok-beta">Grok Beta</SelectItem>
                    <SelectItem value="gemini-3.1-pro">Gemini 3.1 Pro (Reasoning Flagship)</SelectItem>
                    <SelectItem value="gemini-3.1-flash">Gemini 3.1 Flash (Extreme Speed)</SelectItem>
                    <SelectItem value="gemini-3-flash-preview">Gemini 3.0 Flash Preview</SelectItem>
                    <SelectItem value="gemini-3-pro-preview">Gemini 3.0 Pro Preview</SelectItem>
                    <SelectItem value="gpt-5.5">GPT-5.5 (Fastest Voice / Flagship)</SelectItem>
                    <SelectItem value="gpt-5.4-mini">GPT-5.4 Mini (High Speed)</SelectItem>
                    <SelectItem value="gpt-4.1-2025-04-14">GPT-4.1 (Latest)</SelectItem>
                    <SelectItem value="gpt-4-turbo">GPT-4 Turbo</SelectItem>
                    <SelectItem value="gpt-4">GPT-4</SelectItem>
                    <SelectItem value="gpt-3.5-turbo">GPT-3.5 Turbo</SelectItem>
                    <SelectItem value="o3">OpenAI o3 (Deep Reasoning)</SelectItem>
                    <SelectItem value="o1">OpenAI o1</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-4 pt-4 border-t">
                <Label className="text-lg font-semibold flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-purple-500" />
                  Generation Options
                </Label>
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <input 
                      type="checkbox" 
                      id="tight-patterns" 
                      checked={tightPatterns}
                      onChange={(e) => setTightPatterns(e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                    <Label htmlFor="tight-patterns" className="font-medium cursor-pointer">Tight Generated Language Patterns (Max 2 sentences)</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input 
                      type="checkbox" 
                      id="strict-kb" 
                      checked={strictKb}
                      onChange={(e) => setStrictKb(e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                    <Label htmlFor="strict-kb" className="font-medium cursor-pointer">Strict Knowledge Base Adherence (Prioritize KB over internal sales logic)</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input 
                      type="checkbox" 
                      id="use-behavioral-playbook" 
                      checked={useBehavioralPlaybook}
                      onChange={(e) => setUseBehavioralPlaybook(e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                    <Label htmlFor="use-behavioral-playbook" className="font-medium cursor-pointer">Inject Native Behavioral Playbook (Calm-Disc objection handling framework)</Label>
                  </div>
                </div>
              </div>

              <div className="space-y-4 pt-4 border-t">
                <Label className="text-lg font-semibold flex items-center gap-2">
                  <Settings className="h-5 w-5 text-gray-500" />
                  Agent Runtime Settings
                </Label>
                <p className="text-sm text-gray-500">These settings will be applied to the newly created agent for its live calls.</p>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Agent Name</Label>
                    <Input 
                      placeholder="e.g., GLP-1 Sales Agent" 
                      value={agentName}
                      onChange={(e) => setAgentName(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Agent Persona Name <span className="text-xs text-gray-400">(spoken name)</span></Label>
                    <Input 
                      placeholder="e.g., Rachel, Sarah" 
                      value={agentPersonaName}
                      onChange={(e) => setAgentPersonaName(e.target.value)}
                    />
                    <p className="text-xs text-gray-400">The name the agent will introduce itself as on calls. If empty, it will be extracted from the script.</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Runtime LLM</Label>
                    <Select value={runtimeLlm} onValueChange={setRuntimeLlm}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="hermes">Hermes Agent (ChatGPT OAuth)</SelectItem>
                        <SelectItem value="grok-4.3">Grok 4.3 (Latest Flagship)</SelectItem>
                        <SelectItem value="grok-4-1-fast-non-reasoning">Grok 4.1 Fast Non-Reasoning</SelectItem>
                        <SelectItem value="grok-voice-think-fast-1.0">Grok Voice Think Fast 1.0 (Low Latency)</SelectItem>
                        <SelectItem value="grok-4.20-non-reasoning">Grok 4.20 Non-Reasoning (Fast)</SelectItem>
                        <SelectItem value="grok-4-fast-non-reasoning">Grok 4 Fast Non-Reasoning</SelectItem>
                        <SelectItem value="grok-4-fast-reasoning">Grok 4 Fast Reasoning (Deeper Analysis)</SelectItem>
                        <SelectItem value="grok-3">Grok 3</SelectItem>
                        <SelectItem value="grok-2-1212">Grok 2 (Dec 2024)</SelectItem>
                        <SelectItem value="grok-beta">Grok Beta</SelectItem>
                        <SelectItem value="gemini-3.1-pro">Gemini 3.1 Pro (Reasoning Flagship)</SelectItem>
                        <SelectItem value="gemini-3.1-flash">Gemini 3.1 Flash (Extreme Speed)</SelectItem>
                        <SelectItem value="gemini-3-flash-preview">Gemini 3.0 Flash Preview</SelectItem>
                        <SelectItem value="gemini-3-pro-preview">Gemini 3.0 Pro Preview</SelectItem>
                        <SelectItem value="gpt-5.5">GPT-5.5 (Fastest Voice / Flagship)</SelectItem>
                        <SelectItem value="gpt-5.4-mini">GPT-5.4 Mini (High Speed)</SelectItem>
                        <SelectItem value="gpt-4.1-2025-04-14">GPT-4.1 (Latest)</SelectItem>
                        <SelectItem value="gpt-4-turbo">GPT-4 Turbo</SelectItem>
                        <SelectItem value="gpt-4">GPT-4</SelectItem>
                        <SelectItem value="gpt-3.5-turbo">GPT-3.5 Turbo</SelectItem>
                        <SelectItem value="o3">OpenAI o3 (Deep Reasoning)</SelectItem>
                        <SelectItem value="o1">OpenAI o1</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>TTS Provider</Label>
                    <Select value={ttsProvider} onValueChange={setTtsProvider}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cartesia">Cartesia (Ultra-low latency)</SelectItem>
                        <SelectItem value="elevenlabs">ElevenLabs (High quality)</SelectItem>
                        <SelectItem value="hume">Hume AI (Expressive)</SelectItem>
                        <SelectItem value="maya">Maya (Adaptive Emotion)</SelectItem>
                        <SelectItem value="qwen">Qwen TTS (Voice Cloning)</SelectItem>
                        <SelectItem value="sesame">Sesame TTS</SelectItem>
                        <SelectItem value="kokoro">Kokoro TTS (Fast & Free)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>STT Provider</Label>
                    <Select value={sttProvider} onValueChange={setSttProvider}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="deepgram">Deepgram (Fastest)</SelectItem>
                        <SelectItem value="assemblyai">AssemblyAI (High Accuracy)</SelectItem>
                        <SelectItem value="soniox">Soniox</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Voice</Label>
                    <Select value={voice} onValueChange={setVoice}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Rachel">Rachel (Female, Friendly)</SelectItem>
                        <SelectItem value="Drew">Drew (Male, Professional)</SelectItem>
                        <SelectItem value="Clyde">Clyde (Male, Energetic)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Language</Label>
                    <Select value={language} onValueChange={setLanguage}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="English">English</SelectItem>
                        <SelectItem value="Spanish">Spanish</SelectItem>
                        <SelectItem value="French">French</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="flex flex-col items-center justify-center h-full space-y-6">
              <div className="relative">
                <div className="absolute inset-0 border-t-4 border-blue-500 border-solid rounded-full animate-spin h-24 w-24"></div>
                <Bot className="h-24 w-24 text-gray-300 p-4" />
              </div>
              <div className="text-center space-y-2">
                <h3 className="text-xl font-bold text-gray-800">Designing Your Agent</h3>
                <p className="text-gray-500 animate-pulse">{progressText}</p>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="flex flex-col items-center justify-center h-full space-y-4 py-6">
              <div className="bg-green-100 p-4 rounded-full">
                <Sparkles className="h-12 w-12 text-green-500" />
              </div>
              <div className="text-center space-y-2">
                <h3 className="text-2xl font-bold text-gray-800">Agent Saved!</h3>
                <p className="text-gray-500">Your flow has been generated and saved with creator QA results.</p>
                <p className="text-sm text-yellow-600 bg-yellow-50 p-2 rounded max-w-lg mx-auto">
                  Note: If your flow required external APIs, look for nodes labeled [FILLER NODE] to add your webhook URLs.
                </p>
              </div>

              {createdAgent && createdAgent.creator_quality_report && (
                <div className="w-full max-w-2xl bg-white border border-gray-200 rounded-lg p-5 text-left shadow-sm">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h4 className="font-bold text-gray-800 flex items-center gap-2">
                        <AlertCircle className="h-5 w-5 text-blue-500" />
                        Creator QA Score
                      </h4>
                      <p className="text-sm text-gray-500 mt-1">
                        {createdAgent.creator_quality_report.summary?.final_gate_can_deploy
                          ? "This agent is marked deployable by the creator checks."
                          : "Review required before deploying this agent live."}
                      </p>
                    </div>
                    <div className={`text-2xl font-bold px-3 py-2 rounded-md border ${
                      createdAgent.creator_quality_report.score >= 10
                        ? 'text-green-700 bg-green-50 border-green-200'
                        : createdAgent.creator_quality_report.score >= 8
                          ? 'text-yellow-700 bg-yellow-50 border-yellow-200'
                          : 'text-red-700 bg-red-50 border-red-200'
                    }`}>
                      {createdAgent.creator_quality_report.score_label}
                    </div>
                  </div>

                  {createdAgent.creator_quality_report.top_issues?.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <h5 className="text-xs font-bold text-gray-600 uppercase tracking-wider mb-2">Top Issues</h5>
                      <div className="space-y-2">
                        {createdAgent.creator_quality_report.top_issues.slice(0, 3).map((issue, i) => (
                          <div key={i} className="text-sm bg-gray-50 border border-gray-100 rounded-md p-3">
                            <div className="font-semibold text-gray-800">
                              {issue.node_label || issue.node_id || issue.source}
                            </div>
                            <div className="text-gray-500 text-xs mt-1">
                              {issue.reason || "creator_quality_issue"}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {createdAgent && createdAgent.sandbox_stress_test_report && (
                <div className="w-full max-w-2xl mt-4 bg-gray-50 border border-gray-200 rounded-lg p-5 text-left overflow-y-auto max-h-[40vh]">
                  <h4 className="font-bold text-gray-800 border-b pb-2 mb-4 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Bot className="h-5 w-5 text-blue-500" />
                      Phase 2.9 Stress Test Results for "{createdAgent.name}"
                    </div>
                    <Button 
                      onClick={downloadMarkdownReport}
                      variant="outline" 
                      size="sm" 
                      className="bg-white border-blue-200 text-blue-700 hover:bg-blue-50 shrink-0"
                    >
                      <Download size={16} className="mr-2" />
                      Download MD
                    </Button>
                  </h4>
                  <div className="grid grid-cols-4 gap-3 text-center text-sm mb-5">
                    <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                      <div className="text-gray-500 text-xs font-semibold uppercase tracking-wider mb-1">Tested</div>
                      <div className="text-2xl font-bold text-blue-600">{createdAgent.sandbox_stress_test_report.nodes_tested || 0}</div>
                    </div>
                    <div className="bg-white p-3 rounded-lg border border-green-200 shadow-sm">
                      <div className="text-gray-500 text-xs font-semibold uppercase tracking-wider mb-1">Passed</div>
                      <div className="text-2xl font-bold text-green-600">{createdAgent.sandbox_stress_test_report.nodes_passed || 0}</div>
                    </div>
                    <div className="bg-white p-3 rounded-lg border border-yellow-200 shadow-sm">
                      <div className="text-gray-500 text-xs font-semibold uppercase tracking-wider mb-1">Fixed</div>
                      <div className="text-2xl font-bold text-yellow-600">{createdAgent.sandbox_stress_test_report.nodes_fixed || 0}</div>
                    </div>
                    <div className="bg-white p-3 rounded-lg border border-red-200 shadow-sm">
                      <div className="text-gray-500 text-xs font-semibold uppercase tracking-wider mb-1">Failed</div>
                      <div className="text-2xl font-bold text-red-600">{createdAgent.sandbox_stress_test_report.nodes_failed || 0}</div>
                    </div>
                  </div>
                  
                  {createdAgent.sandbox_stress_test_report.details?.filter(d => d.status !== 'passed').length > 0 && (
                    <div className="space-y-3">
                      <h5 className="text-sm font-bold text-gray-700">Flagged Nodes (Auto-Fixed or Failed):</h5>
                      {createdAgent.sandbox_stress_test_report.details.filter(d => d.status !== 'passed').map((d, i) => (
                        <div key={i} className={`p-3 rounded-md text-sm border shadow-sm ${d.status === 'failed' ? 'bg-red-50 border-red-200' : 'bg-yellow-50 border-yellow-200'}`}>
                          <div className="font-bold text-gray-800 flex justify-between items-center">
                            <span>{d.node_label || d.node_id}</span>
                            <span className={`text-xs px-2 py-1 rounded-full font-bold text-white ${d.status === 'failed' ? 'bg-red-500' : 'bg-yellow-500'}`}>
                              {d.status.toUpperCase()}
                            </span>
                          </div>
                          <div className="flex justify-between mt-2 pt-2 border-t border-black/5">
                            <span className="text-gray-600">Rewrites: <span className="font-semibold">{d.rewrites}</span></span>
                            <span className="text-gray-600">Avg Latency: <span className="font-semibold">{d.final_latency_ms}ms</span></span>
                          </div>
                          {d.failure_history && d.failure_history.length > 0 && (
                            <div className="mt-3 pt-3 border-t border-black/5">
                              <span className="text-xs font-bold text-gray-700 block mb-2">Final Iteration Failure Reason:</span>
                              {d.failure_history[d.failure_history.length - 1].failures.map((f, fi) => (
                                <div key={fi} className="bg-white/60 p-2 rounded border border-red-100 text-xs mb-1.5 break-words">
                                  <div className="font-mono text-[10px] text-gray-500 mb-1 leading-tight">Input: "{f.test_input}"</div>
                                  <div className="flex items-center gap-2">
                                    <span className="text-gray-500">Expected: <span className="font-bold text-blue-600">T:{f.expected}</span></span>
                                    <span className="text-gray-400">→</span>
                                    <span className="text-gray-500">Actual: <span className="font-bold text-red-600">T:{f.actual}</span></span>
                                    {!f.correct && <span className="bg-red-100 text-red-600 px-1 rounded text-[10px] ml-auto">WRONG</span>}
                                    {!f.fast && <span className="bg-orange-100 text-orange-600 px-1 rounded text-[10px] ml-auto">SLOW ({f.ttft_ms}ms)</span>}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="border-t pt-4 mt-auto">
          {step === 1 && (
            <>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={() => setStep(2)}>Next: Configuration</Button>
            </>
          )}
          {step === 2 && (
            <>
              <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
              <Button onClick={handleGenerate} className="bg-purple-600 hover:bg-purple-700 text-white flex gap-2">
                <Sparkles className="h-4 w-4" /> Generate Perfect Agent
              </Button>
            </>
          )}
          {step === 4 && (
            <Button onClick={resetAndClose} className="w-full">Open in FlowBuilder</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AgentCreatorModal;
